// cellarMerge.js — duplicate detection for the household merge-cellars flow.
//
// When a household member accepts an invite, their cellar gets folded into
// the inviter's. We don't auto-merge potential duplicates (same wine in
// both cellars) — instead we surface them for review. This module is the
// detection helper that powers that review UI: given a list of wines, it
// returns four buckets describing what action the merge UI should offer.
//
// Buckets (highest confidence → lowest):
//   - strong   : exact name + producer + vintage match (case/accent-insensitive)
//   - probable : name + producer match, any vintage (NV vs 2019, etc.)
//                OR name + concrete vintage match, producer differs
//                (covers AI-scan producer typos + parent-vs-brand naming)
//   - possible : same producer, fuzzy name (Sørensen-Dice ≥0.85)
//   - clean    : no match — flow into the shared cellar unreviewed
//
// The merge UI defaults strong/probable to "treat as duplicate" (review
// required for which side to keep) and possible to "treat as separate"
// (user can flag any as duplicate manually).
//
// detectDuplicatePairs takes a single wines array and finds ALL pairs,
// including self-self pairs (the same user added the same wine twice).
// An optional currentUserId orients cross-user pairs as { mine, theirs }
// from the caller's perspective; self-self pairs are flagged selfSelf:true
// so the review UI can swap labels ("Yours #1" vs "Yours #2").
//
// See docs/design/household-merge-cellars.md §7.

/**
 * Bucket a wines array into duplicate confidence tiers.
 *
 * Each wine appears in at most one pair (greedy by input order, strongest
 * tier per pair). This avoids the merge UI surfacing 3 review rows for a
 * 3-way duplicate cluster — the user resolves one pair at a time anyway.
 *
 * @param {Array<Object>} wines           — all wines to scan (single cellar)
 * @param {string|null}   currentUserId   — caller's uid, for {mine,theirs} orientation
 * @returns {{
 *   strong:   Array<{ mine, theirs, selfSelf? }>,
 *   probable: Array<{ mine, theirs, selfSelf? }>,
 *   possible: Array<{ mine, theirs, score, selfSelf? }>,
 *   clean:    Array<Object>
 * }}
 */
export function detectDuplicatePairs(wines, currentUserId = null) {
  const strong   = []
  const probable = []
  const possible = []
  const inPair   = new Set()

  // Index by strong key for O(1) lookups in the strong-tier pass below.
  // We still scan O(n²) for probable/possible since those compare on
  // multiple fields and short-circuiting on a key doesn't apply.
  const byStrongKey = new Map()
  for (const w of wines) {
    const k = strongKey(w)
    if (k && !byStrongKey.has(k)) byStrongKey.set(k, w)
  }

  // Pass 1: strong matches. Pair every wine that shares a strong key
  // with another wine (greedy — each wine in one pair).
  for (let i = 0; i < wines.length; i++) {
    const a = wines[i]
    if (inPair.has(a.id || a)) continue
    const ka = strongKey(a)
    if (!ka) continue
    for (let j = i + 1; j < wines.length; j++) {
      const b = wines[j]
      if (inPair.has(b.id || b)) continue
      if (strongKey(b) !== ka) continue
      strong.push(orderPair(a, b, currentUserId))
      inPair.add(a.id || a)
      inPair.add(b.id || b)
      break
    }
  }

  // Pass 2: probable matches.
  // Either qualifies:
  //   A. name + producer match, vintage differs (NV vs 2019, etc.)
  //   B. name + concrete vintage match, producer differs
  //      (covers AI-scan producer typos like "Lodi Wine Company" vs
  //      "Sixth Sense Wines", and parent-vs-brand naming like
  //      "Folie à Deux Winery" vs "Ménage à Trois Wines").
  //      Vintage MUST be a real year on both sides — exact "2023"
  //      match is strong enough to deduplicate even without producer.
  //      We don't allow NV/empty vintages here because two wines
  //      both named "Reserve" with no vintage from different
  //      producers shouldn't auto-bucket as the same wine.
  for (let i = 0; i < wines.length; i++) {
    const a = wines[i]
    if (inPair.has(a.id || a)) continue
    for (let j = i + 1; j < wines.length; j++) {
      const b = wines[j]
      if (inPair.has(b.id || b)) continue
      if (!eqStr(a.name, b.name)) continue
      let isProbable = false
      // Pattern A: producer match
      if (eqStr(a.producer, b.producer)) {
        isProbable = true
      } else {
        // Pattern B: concrete vintage match (both non-empty, not NV)
        const aVin = String(a.vintage || '').trim()
        const bVin = String(b.vintage || '').trim()
        if (aVin && bVin
            && aVin.toLowerCase() !== 'nv'
            && bVin.toLowerCase() !== 'nv'
            && eqStr(aVin, bVin)) {
          isProbable = true
        }
      }
      if (isProbable) {
        probable.push(orderPair(a, b, currentUserId))
        inPair.add(a.id || a)
        inPair.add(b.id || b)
        break
      }
    }
  }

  // Pass 3: possible matches — same producer, fuzzy name ≥0.85. Lowest
  // confidence — review-required, default to "keep both" in the UI.
  for (let i = 0; i < wines.length; i++) {
    const a = wines[i]
    if (inPair.has(a.id || a)) continue
    for (let j = i + 1; j < wines.length; j++) {
      const b = wines[j]
      if (inPair.has(b.id || b)) continue
      if (!eqStr(a.producer, b.producer)) continue
      const score = fuzzyName(a.name, b.name)
      if (score >= 0.85) {
        const pair = orderPair(a, b, currentUserId)
        pair.score = score
        possible.push(pair)
        inPair.add(a.id || a)
        inPair.add(b.id || b)
        break
      }
    }
  }

  const clean = wines.filter(w => !inPair.has(w.id || w))
  return { strong, probable, possible, clean }
}

/**
 * Decide which side of a pair gets the `mine` label vs `theirs`.
 *
 * - Cross-user with currentUserId set: caller's wine is `mine`.
 * - Self-self (both owned by currentUserId): older wine is `mine` and we
 *   stamp selfSelf:true so the review UI can use neutral labels.
 * - No currentUserId or neither side owned by caller: stable id-order.
 */
function orderPair(a, b, currentUserId) {
  const aMine = currentUserId && a.userId === currentUserId
  const bMine = currentUserId && b.userId === currentUserId
  if (aMine && bMine) {
    // Order by createdAt seconds when available, falling back to id.
    const aT = a?.createdAt?.seconds ?? 0
    const bT = b?.createdAt?.seconds ?? 0
    const aFirst = aT !== bT ? aT < bT : (a.id || '') < (b.id || '')
    return aFirst
      ? { mine: a, theirs: b, selfSelf: true }
      : { mine: b, theirs: a, selfSelf: true }
  }
  if (aMine) return { mine: a, theirs: b }
  if (bMine) return { mine: b, theirs: a }
  return (a.id || '') < (b.id || '')
    ? { mine: a, theirs: b }
    : { mine: b, theirs: a }
}

/**
 * Build the "strong" identity key for a wine. Returns null if the wine
 * lacks the minimum identity fields (name + producer); a wine with only
 * a name but no producer can't be safely deduped at the strong-confidence
 * level.
 */
export function strongKey(w) {
  if (!w?.name || !w?.producer) return null
  return [
    normalize(w.name),
    normalize(w.producer),
    normalize(w.vintage || 'NV'),
  ].join('|')
}

/**
 * Case- AND accent-insensitive equality. Critical for international wine
 * names: "Ménage à Trois" and "Menage a Trois" must match. NFD-decomposes
 * Unicode then strips combining marks (the diacritics) before lowercasing.
 */
function eqStr(a, b) {
  return normalize(a) === normalize(b)
}

/**
 * Normalize a string for fuzzy comparison: trim, NFD-decompose, drop
 * combining marks (accents), lowercase. "Ménage" → "menage".
 */
function normalize(s) {
  return String(s ?? '')
    .normalize('NFD')
    // Strip Unicode combining marks (U+0300..U+036F) using the Mn property.
    // Hex escape avoids the literal-character range tripping over source-file
    // encoding edits.
    .replace(/\p{M}/gu, '')
    .trim()
    .toLowerCase()
}

/**
 * Normalized similarity score for two wine names, 0..1.
 *
 * Implementation: Sørensen-Dice on character bigrams. Picked over
 * Levenshtein because it handles word-order swaps well (Bordeaux vs
 * Bordeaux Blend, Tenuta San Guido vs Sassicaia Tenuta) and doesn't
 * need a length-normalization step. Roll-our-own to avoid pulling in a
 * dependency for a single helper.
 *
 * Returns 1.0 for identical strings, 0.0 for no common bigrams.
 */
export function fuzzyName(a, b) {
  const sa = normalize(a)
  const sb = normalize(b)
  if (!sa && !sb) return 1
  if (!sa || !sb) return 0
  if (sa === sb) return 1
  const ba = bigrams(sa)
  const bb = bigrams(sb)
  if (ba.size === 0 || bb.size === 0) return 0
  let intersection = 0
  for (const g of ba) if (bb.has(g)) intersection++
  return (2 * intersection) / (ba.size + bb.size)
}

function bigrams(s) {
  const out = new Set()
  // Strip punctuation + collapse whitespace before bigramming so
  // "St. Émilion" and "Saint Emilion" overlap more than character-level
  // would suggest. (Input is already accent-stripped via normalize().)
  const cleaned = s.replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim()
  for (let i = 0; i < cleaned.length - 1; i++) {
    out.add(cleaned.slice(i, i + 2))
  }
  return out
}
